// Where the product tour can actually run, and how callers hand the user back.
//
// Every tour step after the welcome card spotlights a live element: the chat
// composer, the drawer hamburger, the drawer/sidebar rows. Only the chat
// surface has all of them, so each entry point that starts the tour (the
// post-onboarding welcome on both platforms, the auto-start that covers
// migrated users) has to route there first.
//
// That redirect used to be one-way: onboarding's final CTA says "Find jobs"
// and lands on /(app)/jobs (issue #257), the tour dragged the user to chat,
// and nothing brought them back - on web the welcome gate was mounted only
// under the chat route, so a user who landed on jobs got no welcome and no
// tour at all. Pair every redirect to TOUR_SURFACE_HREF with
// parkPostTourDestination(getReturnableTourDestination(...)) so
// TourProvider.finish() returns the user to where they were headed once the
// tour ends, completed or skipped.
//
// Pure module (no react-native / expo-router imports) so it stays unit
// testable under the node test environment.
import { isChatRoutePath, normalizeRoutePath } from './chat-navigation';

export const TOUR_SURFACE_HREF = '/(app)/chat';

/**
 * True when the current route is the chat surface the tour spotlights.
 * `segmentsKey` is the expo-router segments join: it covers the window during
 * a transition where the segments have flipped but the pathname has not.
 */
export function isTourSurfaceRoute(pathname: string, segmentsKey: string) {
  return (
    isChatRoutePath(pathname) || segmentsKey.split('/').includes('chat')
  );
}

// Routes we must never hand a user back to after the tour: they are either
// outside the signed-in app or transient shims that immediately redirect
// again. Returning to one of these would either trap the user (auth,
// onboarding) or bounce them through a handoff a second time. Compared after
// normalizeRoutePath, so route groups are already stripped.
const NON_RETURNABLE_PREFIXES = [
  '/auth',
  '/auth-complete',
  '/login',
  '/register',
  '/onboarding',
  '/resume-import',
  '/jobs-handoff',
  '/skills-handoff',
  '/post-onboarding-welcome',
];

/**
 * Serialize the route's search params into a query string for the parked
 * destination. expo-router's usePathname() strips the query, but some
 * destinations only work with it (resume-import lands on /cv with
 * resumeImportNotice + resumeImportSkillId, which carry the "apply this
 * skill" CTA) — dropping it silently loses the notice when the tour
 * intercepts the redirect. Route params from dynamic segments are already in
 * the pathname, so only plain string/string[] values are kept.
 */
export function serializeTourSearchParams(
  params: Record<string, unknown> | null | undefined,
): string {
  if (!params) return '';
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string') {
      query.append(key, value);
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string') query.append(key, item);
      }
    }
  }
  return query.toString();
}

/**
 * The href to restore after the tour, or null when the current route isn't
 * worth returning to (the tour surface itself, the root, anything outside the
 * personal app). Callers pass the result straight to parkPostTourDestination,
 * which ignores nulls. `search` is an optional pre-serialized query string
 * (see serializeTourSearchParams) appended to the returned href.
 */
export function getReturnableTourDestination(
  pathname: string,
  segmentsKey: string,
  search?: string,
): string | null {
  if (isTourSurfaceRoute(pathname, segmentsKey)) return null;

  const path = normalizeRoutePath(pathname);
  if (path === '/') return null;
  if (
    NON_RETURNABLE_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    )
  ) {
    return null;
  }

  // The tour only mounts inside the (app) group; if the segments say otherwise
  // (an employer route sneaking through a transition) don't park it.
  const segments = segmentsKey.split('/').filter(Boolean);
  if (segments.length > 0 && !segments.includes('(app)')) return null;

  return search ? `${path}?${search}` : path;
}
